"use client";

import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, ChevronDown, Sparkles, TrendingUp } from 'lucide-react';
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Link from "next/link";
import SponsoredPropertyGrid from '@/components/features/listings/SponsoredPropertyGrid';
import { sponsoredListings } from '@/components/features/listings/mockData';

// Create a more extensive version of sponsored listings by duplicating existing ones
// and adding the "sponsored: true" flag
const generateSponsoredProperties = () => {
  // Create copies with new IDs and slight variations
  const additionalProperties = sponsoredListings.map((property, index) => {
    // Create a deep copy
    const newProperty = {
      ...property,
      id: `spon-extended-${index + 1}`,
      sponsored: true, // Add the sponsored flag required by specs
      title: property.title + (index % 2 === 0 ? ' with garden' : ' with balcony'),
      // Use deterministic values instead of random ones
      price: property.price * (0.9 + (index * 0.06) % 0.3),
    };
    
    // Add review data in a deterministic way
    if (index % 2 === 0) {
      newProperty.reviewCount = 5 + (index * 2) % 20;
      newProperty.averageRating = 4 + (index * 0.1) % 1;
    }
    
    // Deterministic property features
    if (index % 3 === 0) {
      newProperty.isNew = true;
    } else {
      newProperty.daysListed = 1 + (index % 14);
    }
    
    return newProperty;
  });
  
  // Add the "sponsored: true" flag to all original properties
  const originalWithSponsored = sponsoredListings.map(property => ({
    ...property,
    sponsored: true
  }));
  
  // Combine and return all sponsored properties
  return [...originalWithSponsored, ...additionalProperties];
};

export default function SponsoredPageClient() {
  const [isVisible, setIsVisible] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const sponsoredProperties = useMemo(() => {
    // Generate and filter sponsored properties once; deterministic so safe for SSR
    return generateSponsoredProperties().filter((property) => property.sponsored === true);
  }, []);
  
  // Trigger entrance animations once on the client
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-linear-to-b from-white to-blue-50/30">
      <Navbar initialAuth={{ isAuthenticated: false, userType: null }} />
      
      <div className="container mx-auto px-4 pt-28 pb-12">
        <main className="grow">
          {/* Hero Section with dynamic gradient */}
          <motion.div 
            className="relative mb-10 md:mb-16 overflow-hidden rounded-2xl bg-linear-to-r from-blue-600 to-blue-400 text-white"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : -20 }}
                            transition={{ duration: 0.6, ease: "easeOut" as const }}
          >
            {/* Animated particles - with deterministic positions */}
            <div className="absolute inset-0 overflow-hidden">
              {Array.from({ length: 20 }).map((_, i) => {
                // Use deterministic values based on the index
                const xPos1 = `${(i * 5) % 100}%`;
                const xPos2 = `${((i * 7) + 20) % 100}%`;
                const xPos3 = `${((i * 11) + 40) % 100}%`;
                
                const yPos1 = `${(i * 7) % 100}%`;
                const yPos2 = `${((i * 13) + 30) % 100}%`;
                const yPos3 = `${((i * 17) + 60) % 100}%`;
                
                const duration = 10 + (i % 10) * 2;
                
                return (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 rounded-full bg-white/30"
                    initial={{ 
                      x: xPos1,
                      y: yPos1
                    }}
                    animate={{ 
                      x: [xPos1, xPos2, xPos3],
                      y: [yPos1, yPos2, yPos3],
                      opacity: [0.2, 0.8, 0.2]
                    }}
                    transition={{ 
                      duration,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  />
                );
              })}
            </div>
            
            <div className="relative p-8 md:p-12 lg:p-16 text-center md:text-left">
              <Badge className="mb-4 bg-white/20 text-white border-white/30 backdrop-blur-sm px-3 py-1.5">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                Premium Selection
              </Badge>
              
              <h1 className="text-3xl md:text-5xl font-bold mb-4 text-white">
                Sponsored Properties
              </h1>
              
              <p className="text-lg md:text-xl text-white/90 max-w-2xl">
                Discover our curated collection of premium sponsored properties available across Nigeria. These exclusive listings are featured by our trusted partners.
              </p>
              
              <div className="mt-8 flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-white text-blue-700 hover:bg-white/90">
                  <Link href="/listings">
                    Explore Properties
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                
                <Button 
                  asChild
                  variant="default" 
                  size="lg" 
                  className="bg-blue-700 hover:bg-blue-800 text-white z-10"
                >
                  <Link href="/ads">
                    Learn About Sponsorship
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
          
          {/* Stats Section */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12 md:mb-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
                            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" as const }}
          >
            {[
              { label: 'Premium Properties', value: sponsoredProperties.length },
              { label: 'Trusted Partners', value: 12 },
              { label: 'Cities Covered', value: 8 },
              { label: 'Happy Tenants', value: '3,500+' },
            ].map((stat, index) => (
              <div 
                key={index} 
                className="bg-white/50 backdrop-blur-sm rounded-xl p-6 text-center border border-blue-100 hover:shadow-md transition-shadow"
              >
                <div className="text-2xl md:text-3xl font-bold text-blue-700 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 text-sm md:text-base">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
          
          {/* Why Sponsored Properties Section */}
          <motion.div
            className="mb-16 md:mb-24"
            initial={{ opacity: 0 }}
            animate={{ opacity: isVisible ? 1 : 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="h-6 w-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-800">Why Sponsored Properties?</h2>
              <div className="h-px flex-1 bg-linear-to-r from-blue-200 to-transparent"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Premium Quality',
                  description: 'Our sponsored properties undergo rigorous verification and quality checks.'
                },
                {
                  title: 'Exclusive Access',
                  description: 'Get early access to premium properties before they hit the open market.'
                },
                {
                  title: 'Enhanced Visibility',
                  description: 'Sponsored properties receive maximum exposure to potential tenants.'
                }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="bg-white p-6 rounded-xl border border-blue-100 hover:border-blue-300 transition-colors"
                >
                  <div className="font-bold text-lg text-gray-900 mb-2">{item.title}</div>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
            
            <AnimatePresence>
              {showMore && (
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {[
                    {
                      title: 'Virtual Tours',
                      description: 'Most sponsored properties offer immersive virtual tour experiences.'
                    },
                    {
                      title: 'Dedicated Support',
                      description: 'Get priority customer service for all sponsored listings.'
                    },
                    {
                      title: 'Verified Landlords',
                      description: 'All sponsored property owners undergo thorough background checks.'
                    }
                  ].map((item, index) => (
                    <div 
                      key={index}
                      className="bg-white p-6 rounded-xl border border-blue-100 hover:border-blue-300 transition-colors"
                    >
                      <div className="font-bold text-lg text-gray-900 mb-2">{item.title}</div>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
            
            <div className="mt-6 text-center">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowMore(!showMore)}
                className="text-blue-700 border-blue-200"
              >
                {showMore ? 'Show Less' : 'Show More'}
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${showMore ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          </motion.div>
          
          {/* Main Sponsored Properties Grid */}
          {sponsoredProperties.length > 0 && (
            <SponsoredPropertyGrid 
              title="Sponsored Properties"
              subtitle="Browse our collection of premium sponsored properties across Nigeria"
              properties={sponsoredProperties}
            />
          )}
        </main>
      </div>
      
      <Footer />
    </div>
  );
} 